// SAR ELT beacon render (M36.1, SAR-2/3): each deployed beacon is a small orange
// device box with a thin vertical beam so a search crew can spot it once close.
// Client-only (ENG-1); the sim owns the beacon positions + the signal model.
import * as THREE from 'three';
import type { Beacon } from '../../sim/education/sar';

const DEVICE = 0xff7a1a; // hi-vis orange
const BEAM = 0xffd54a;

export class BeaconRenderer {
  private readonly group = new THREE.Group();
  private readonly rigs = new Map<number, THREE.Group>();

  constructor(scene: THREE.Scene) { scene.add(this.group); }

  private buildRig(): THREE.Group {
    const rig = new THREE.Group();
    const box = new THREE.Mesh(
      new THREE.BoxGeometry(0.5, 0.5, 0.5),
      new THREE.MeshLambertMaterial({ color: DEVICE, emissive: DEVICE, emissiveIntensity: 0.4 }),
    );
    box.position.y = 0.25;
    const antenna = new THREE.Mesh(
      new THREE.CylinderGeometry(0.03, 0.03, 0.8, 6),
      new THREE.MeshLambertMaterial({ color: 0x222222 }),
    );
    antenna.position.y = 0.9;
    // A faint beam upward so it reads at a distance (transparent, always-visible-ish).
    const beam = new THREE.Mesh(
      new THREE.CylinderGeometry(0.08, 0.08, 24, 6, 1, true),
      new THREE.MeshBasicMaterial({ color: BEAM, transparent: true, opacity: 0.18, depthWrite: false, side: THREE.DoubleSide }),
    );
    beam.position.y = 12;
    rig.add(box, antenna, beam);
    return rig;
  }

  update(beacons: readonly Beacon[]): void {
    const seen = new Set<number>();
    for (const b of beacons) {
      seen.add(b.id);
      let rig = this.rigs.get(b.id);
      if (!rig) { rig = this.buildRig(); this.rigs.set(b.id, rig); this.group.add(rig); }
      rig.position.set(b.x, b.y, b.z);
    }
    for (const [id, rig] of this.rigs) {
      if (seen.has(id)) continue;
      this.group.remove(rig);
      rig.traverse((o) => { if (o instanceof THREE.Mesh) o.material.dispose(); });
      this.rigs.delete(id);
    }
  }

  clear(): void {
    for (const rig of this.rigs.values()) {
      this.group.remove(rig);
      rig.traverse((o) => { if (o instanceof THREE.Mesh) o.material.dispose(); });
    }
    this.rigs.clear();
  }
}
